import styled, { css } from 'styled-components'

interface CardContainerProps {
  $variant: 'default' | 'compact' | 'kanban' | 'minimal'
  $size: 'small' | 'medium' | 'large'
  $selected: boolean
  $draggable: boolean
}

const sizeStyles = {
  small: css`
    padding: 10px;
    min-height: 80px;
  `,
  medium: css`
    padding: 12px;
    min-height: 112px;
  `,
  large: css`
    padding: 16px;
    min-height: 144px;
  `,
}

const variantStyles = {
  default: css`
    background: white;
    border: 1px solid ${({ theme }) => theme.colors.border.default};
  `,
  compact: css`
    background: white;
    border: 1px solid ${({ theme }) => theme.colors.border.default};
    padding: 10px;
  `,
  kanban: css`
    background: white;
    border: none;
    box-shadow: ${({ theme }) => theme.shadows.sm};
    margin-bottom: 6px;
  `,
  minimal: css`
    background: transparent;
    border: 1px solid ${({ theme }) => theme.colors.border.default};
  `,
}

export const CardContainer = styled.div<CardContainerProps>`
  position: relative;
  border-radius: ${({ theme }) => theme.borderRadius.medium};
  transition: all ${({ theme }) => theme.transitions.fast};
  cursor: pointer;

  ${({ $size }) => sizeStyles[$size]}
  ${({ $variant }) => variantStyles[$variant]}

  ${({ $selected }) =>
    $selected &&
    css`
      border-color: ${({ theme }) => theme.colors.primary};
      background: ${({ theme }) => theme.colors.primaryLight};
    `}

  ${({ $draggable }) =>
    $draggable &&
    css`
      cursor: grab;

      &:active {
        cursor: grabbing;
      }
    `}

  &:hover {
    transform: translateY(-1px);
    box-shadow: ${({ theme }) => theme.shadows.md};
  }
`

export const CardDragHandle = styled.div`
  position: absolute;
  top: 6px;
  left: 6px;
  opacity: 0;
  transition: opacity ${({ theme }) => theme.transitions.fast};
  cursor: grab;

  ${CardContainer}:hover & {
    opacity: 0.6;
  }

  &:hover {
    opacity: 1 !important;
  }
`

export const CardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 10px;
`

export const CardTitle = styled.h3`
  margin: 0;
  font-size: ${({ theme }) => theme.typography.fontSize.body};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.text.primary};
  line-height: 1.3;
`

export const CardMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 3px;
  font-size: ${({ theme }) => theme.typography.fontSize.small};
  color: ${({ theme }) => theme.colors.text.secondary};
  margin-top: 3px;

  svg {
    width: 11px;
    height: 11px;
  }
`

export const CardPriority = styled.div<{ $color: string }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  border-radius: ${({ theme }) => theme.borderRadius.small};
  background: ${({ $color }) => `${$color}15`};

  svg {
    width: 13px;
    height: 13px;
  }
`

export const CardBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-bottom: 10px;
`

export const CardAmount = styled.div`
  font-size: ${({ theme }) => theme.typography.fontSize.title};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.primary};
  margin-top: 3px;
`

export const CardOwner = styled.div`
  display: flex;
  align-items: center;
  gap: 5px;
  padding: 3px 6px;
  background: ${({ theme }) => theme.colors.background};
  border-radius: ${({ theme }) => theme.borderRadius.small};
  width: fit-content;

  svg {
    width: 11px;
    height: 11px;
    color: ${({ theme }) => theme.colors.text.secondary};
  }
`

export const CardFooter = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: auto;
`

export const CardTags = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 3px;
  margin-top: 6px;
`

export const CardActions = styled.div`
  display: flex;
  gap: 3px;

  button {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 22px;
    height: 22px;
    padding: 0;
    background: transparent;
    border: none;
    border-radius: ${({ theme }) => theme.borderRadius.small};
    cursor: pointer;
    transition: all ${({ theme }) => theme.transitions.fast};

    svg {
      width: 13px;
      height: 13px;
      color: ${({ theme }) => theme.colors.text.secondary};
    }

    &:hover {
      background: ${({ theme }) => theme.colors.background};

      svg {
        color: ${({ theme }) => theme.colors.text.primary};
      }
    }
  }
`
